/* ==========================================================================
   Noveliya — Product info drawer
   Overlay is created entirely in JS with inline styles so no CSS cascade
   issue can ever prevent it from rendering.
   ========================================================================== */

(function () {
  'use strict';

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), ' +
    'select:not([disabled]), textarea:not([disabled]), ' +
    '[tabindex]:not([tabindex="-1"])';

  class NvDrawer {
    constructor(sectionId) {
      this.id     = sectionId;
      this.isOpen = false;

      this.drawer = document.getElementById(`NvDrawer-${sectionId}`);
      if (!this.drawer) return;

      this.titleEl  = this.drawer.querySelector('.nv-drawer__title');
      this.bodyEl   = this.drawer.querySelector('.nv-drawer__body');
      this.closeBtn = this.drawer.querySelector('[data-drawer-close]');

      this._lastTrigger = null;
      this._onKeyDown   = this._handleKeyDown.bind(this);

      /* Overlay — all styles set inline so the CSS cascade cannot interfere */
      this._overlay = this._buildOverlay();

      /* Append both to body so they participate in the root stacking context */
      document.body.appendChild(this._overlay);
      document.body.appendChild(this.drawer);

      this._bindTriggers();
      this._bindClose();
    }

    /* ── Overlay factory ──────────────────────────────────────────────────── */

    _buildOverlay() {
      const el = document.createElement('div');
      el.setAttribute('aria-hidden', 'true');
      el.setAttribute('role', 'presentation');
      el.setAttribute('data-nv-overlay', '');

      /* display:none keeps it completely inert until open() activates it */
      el.style.cssText = [
        'display:none',
        'position:fixed',
        'top:0',
        'left:0',
        'background:rgba(18,10,3,0.45)',
        'z-index:2147483646',
        'opacity:0',
        'cursor:pointer',
        'transition:opacity 280ms ease'
      ].join(';');

      el.addEventListener('click', () => this.close());
      return el;
    }

    /* ── Public API ───────────────────────────────────────────────────────── */

    open(templateId, trigger) {
      const tpl = document.getElementById(templateId);
      if (!tpl) return;

      this._lastTrigger = trigger || null;

      /* Swap panel content */
      this.bodyEl.innerHTML = '';
      this.bodyEl.appendChild(tpl.content.cloneNode(true));
      if (this.titleEl) this.titleEl.textContent = tpl.dataset.title || '';

      /* Show drawer */
      this.isOpen = true;
      this.drawer.setAttribute('aria-hidden', 'false');
      this.drawer.classList.add('is-open');

      /* Show overlay — explicit px size set at open time to guarantee dimensions */
      const s  = this._overlay.style;
      s.width  = window.innerWidth  + 'px';
      s.height = window.innerHeight + 'px';
      s.display = 'block';
      requestAnimationFrame(() => { s.opacity = '1'; });

      /* Lock scroll — iOS Safari ignores overflow:hidden on body, so we use
         position:fixed (saves + restores scroll position on close) */
      const sbW = window.innerWidth - document.documentElement.clientWidth;
      this._savedScrollY = window.scrollY;
      document.body.style.position    = 'fixed';
      document.body.style.top         = `-${this._savedScrollY}px`;
      document.body.style.width       = '100%';
      document.body.style.overflowY   = 'scroll'; /* prevent layout shift */
      document.body.style.paddingRight = sbW > 0 ? sbW + 'px' : '';

      document.addEventListener('keydown', this._onKeyDown);

      requestAnimationFrame(() => {
        (this.closeBtn || this._getFocusable()[0])?.focus();
      });
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;

      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');

      /* Fade out overlay then hide */
      const s  = this._overlay.style;
      s.opacity = '0';
      setTimeout(() => { s.display = 'none'; }, 280);

      document.body.style.position     = '';
      document.body.style.top          = '';
      document.body.style.width        = '';
      document.body.style.overflowY    = '';
      document.body.style.paddingRight = '';
      window.scrollTo(0, this._savedScrollY || 0);
      document.removeEventListener('keydown', this._onKeyDown);

      if (this._lastTrigger) {
        this._lastTrigger.focus();
        this._lastTrigger = null;
      }
    }

    /* ── Private ──────────────────────────────────────────────────────────── */

    _bindTriggers() {
      document
        .querySelectorAll(`[data-drawer-section="${this.id}"]`)
        .forEach((trigger) => {
          trigger.addEventListener('click', () => {
            this.open(trigger.dataset.drawerTarget, trigger);
          });
        });
    }

    _bindClose() {
      this.closeBtn?.addEventListener('click', () => this.close());
    }

    _handleKeyDown(e) {
      if (e.key === 'Escape') { this.close(); return; }
      if (e.key === 'Tab') this._trapFocus(e);
    }

    _trapFocus(e) {
      const focusable = this._getFocusable();
      if (!focusable.length) return;
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }

    _getFocusable() {
      return [...this.drawer.querySelectorAll(FOCUSABLE)].filter(
        (el) => !el.closest('[hidden]')
      );
    }
  }

  /* ── Init — guard against Theme Editor double-bind ───────────────────────── */

  const initialised = new Set();

  function init() {
    document.querySelectorAll('[data-drawer-section]').forEach((el) => {
      const id = el.dataset.drawerSection;
      if (initialised.has(id)) return;
      if (!document.getElementById(`NvDrawer-${id}`)) return;
      new NvDrawer(id);
      initialised.add(id);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
